import React, { type FC, useCallback, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { EvFlexColumn, EvTabs, EvTitle } from '@evinced-private/ui-common';

import { WFA_DASHBOARD_BUSINESS_UNITS_CONTAINER_DAID } from 'data-automation-ids/WfaDashboardAutomationIds';
import { BusinessUnitSelector } from 'src/apps/dashboard/components/business-unit-selector/BusinessUnitSelector';
import {
	BUSINESS_UNIT_WEB_SDK_TABLE_ID,
	BUSINESS_UNIT_WFA_TABLE_ID
} from 'src/apps/dashboard/constants/Dashboard';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';
import { useDashboardFiltersStore } from 'src/common/stores/dashboardFiltersStore';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { AccessibilitySummaryContainer } from '../../components/accessibility-summary/AccessibilitySummaryContainer';
import { BusinessUnitChartsSection } from '../../components/business-unit-charts-section/BusinessUnitChartsSection';
import { BusinessUnitLoadingStates } from '../../components/business-unit-loading-states/BusinessUnitLoadingStates';
import { DashboardFilters } from '../../components/dashboard-filters/DashboardFilters';
import { useEffectiveFilters } from '../../helpers/FilterHelpers';
import { useBusinessUnitRedirect } from '../../hooks/useBusinessUnitRedirect';
import { useBusinessUnitTabs } from '../../hooks/useBusinessUnitTabs';
import { useConfigurationFilters } from '../../hooks/useConfigurationFilters';
import { useDashboardDataQuery } from '../../hooks/useDashboardDataQuery';
import { useScoreDataQuery } from '../../hooks/useScoreDataQuery';
import { useBusinessUnitsFromConfig } from '../../stores/dashboardConfigStore';
import { DashboardRouteParams } from '../../types/DashboardTypes';

import './DashboardBusinessUnits.scss';

export const DashboardBusinessUnits: FC = () => {
	usePlatformConfigInitializer();
	const { unitId } = useParams<DashboardRouteParams>();
	const history = useHistory();
	const { filters, setFilters } = useDashboardFiltersStore();
	const { businessUnits, isLoading: isConfigLoading } = useBusinessUnitsFromConfig();
	const { isUniquenessServiceEnabled } = usePlatformConfigStore();
	const { filterDefinitions, filterOptions, labelsExcludedFromDashboard } =
		useConfigurationFilters();

	const effectiveFilters = useEffectiveFilters(filters, filterDefinitions);
	const { onTablePageOrPageSizeChange, getTableStateById } = useTableState();

	// Wrapper to reset table pagination when filters are applied
	const handleFilterChange = useCallback(
		(newFilters: IFilterOptions) => {
			const currentDateRange = filters?.dateRange?.[0]?.value;
			const newDateRange = newFilters?.dateRange?.[0]?.value;

			// Reset pagination to page 1 for both tables when date range changes
			if (currentDateRange !== newDateRange) {
				const wfaTableState = getTableStateById(BUSINESS_UNIT_WFA_TABLE_ID);
				const webSdkTableState = getTableStateById(BUSINESS_UNIT_WEB_SDK_TABLE_ID);

				onTablePageOrPageSizeChange(BUSINESS_UNIT_WFA_TABLE_ID, {
					...wfaTableState.paginationInfo,
					page: 1
				});
				onTablePageOrPageSizeChange(BUSINESS_UNIT_WEB_SDK_TABLE_ID, {
					...webSdkTableState.paginationInfo,
					page: 1
				});
			}

			setFilters(newFilters);
		},
		[filters, setFilters, getTableStateById, onTablePageOrPageSizeChange]
	);

	const businessUnitName =
		businessUnits.find((bu) => bu.id === unitId)?.name ?? unitId ?? 'Unknown';

	const { setActiveTabIndex, tabs, tabPanels } = useBusinessUnitTabs({
		businessUnitName,
		effectiveFilters
	});

	useBusinessUnitRedirect({ unitId, businessUnits, history });

	const filtersWithBusinessUnit = useMemo(() => {
		if (!unitId) {
			return effectiveFilters;
		}

		return {
			...effectiveFilters,
			unitId: [{ value: unitId, label: businessUnitName }]
		};
	}, [effectiveFilters, unitId, businessUnitName]);

	const {
		data: dashboardDataResult,
		isLoading: isDashboardDataLoading,
		error: dashboardDataError
	} = useDashboardDataQuery(
		filtersWithBusinessUnit,
		labelsExcludedFromDashboard,
		filterDefinitions
	);

	const {
		data: scoreData,
		isLoading: isScoreDataLoading,
		error: scoreDataError
	} = useScoreDataQuery(filtersWithBusinessUnit, labelsExcludedFromDashboard, filterDefinitions);

	const scoreDataList = scoreData?.scores ?? [];

	// Handle loading and error states
	const loadingComponent = useMemo(
		() => (
			<BusinessUnitLoadingStates
				isConfigLoading={isConfigLoading}
				businessUnits={businessUnits}
				unitId={unitId}
			/>
		),
		[isConfigLoading, businessUnits, unitId]
	);

	// If we have a loading state to show, return it
	if (isConfigLoading || (!isConfigLoading && businessUnits.length === 0) || !unitId) {
		return loadingComponent;
	}

	return (
		<EvFlexColumn gap="XLARGE" dataAutomationId={WFA_DASHBOARD_BUSINESS_UNITS_CONTAINER_DAID}>
			<EvFlexColumn>
				<BusinessUnitSelector />
			</EvFlexColumn>

			<DashboardFilters
				filters={filters}
				onFilterChange={handleFilterChange}
				filterDefinitions={filterDefinitions}
				filterOptions={filterOptions}
			/>

			<EvFlexColumn role="group" ariaLabelledby="accessibility-summary-heading" as="section">
				<AccessibilitySummaryContainer
					scoreData={scoreDataList}
					uniqueIssuesData={dashboardDataResult?.uniqueIssues}
					totalIssuesData={dashboardDataResult?.totalIssues}
					isLoading={isDashboardDataLoading || isScoreDataLoading}
					error={dashboardDataError || scoreDataError}
					isUniquenessServiceEnabled={isUniquenessServiceEnabled}
				/>
			</EvFlexColumn>

			<EvFlexColumn gap="XXLARGE">
				<BusinessUnitChartsSection
					dashboardData={dashboardDataResult?.dailyAllIssues}
					uniqueIssuesData={dashboardDataResult?.dailyUniqueIssues}
					isDashboardDataLoading={isDashboardDataLoading}
					dashboardDataError={dashboardDataError}
					scoreData={scoreDataList}
					isScoreDataLoading={isScoreDataLoading}
					scoreDataError={scoreDataError}
				/>

				<EvFlexColumn
					as="section"
					role="group"
					ariaLabelledby="sub-tabs-heading"
					dataAutomationId="business-unit-sub-tabs-section"
				>
					<EvTitle
						id="sub-tabs-heading"
						titleText="Tests labeled with this business unit"
						className="sub-tabs-title"
					/>
					<div className="sub-tabs-container">
						<EvTabs
							id="business-unit-tabs"
							tabs={tabs}
							tabPanels={tabPanels}
							defaultTab={0}
							onTabSelected={setActiveTabIndex}
							ariaLabel="Business unit sub-tabs"
							dataAutomationId="business-unit-tabs"
						/>
					</div>
				</EvFlexColumn>
			</EvFlexColumn>
		</EvFlexColumn>
	);
};
